import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Zap, Droplets, Moon, Flame, ArrowRight, Activity, Leaf, Clock, CheckCircle2, TrendingDown, Heart, Star, Quote, Hexagon } from 'lucide-react';
/* ─────────────────────────────────────────────
   1. SCIENCE CALLOUT — "El Secreto del Veneno de Abeja"
   Dark green, bold, no images
───────────────────────────────────────────── */
export function ScienceCallout() {
  return <section className="bg-[#1A3009] py-14 relative overflow-hidden" data-id="element-716">
      {/* Honeycomb SVG accent */}
      <svg className="absolute right-0 top-0 opacity-5 w-64 h-64" viewBox="0 0 200 200" fill="none" data-id="element-717">
        <polygon points="100,10 140,35 140,85 100,110 60,85 60,35" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-718" />
        <polygon points="140,35 180,60 180,110 140,135 100,110 100,60" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-719" />
        <polygon points="60,35 100,10 100,60 60,85 20,60 20,10" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-720" />
        <polygon points="100,110 140,135 140,185 100,210 60,185 60,135" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-721" />
      </svg>
      <svg className="absolute left-0 bottom-0 opacity-5 w-48 h-48" viewBox="0 0 200 200" fill="none" data-id="element-722">
        <polygon points="100,10 140,35 140,85 100,110 60,85 60,35" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-723" />
        <polygon points="60,85 100,110 100,160 60,185 20,160 20,110" stroke="#D4AF37" strokeWidth="2" fill="none" data-id="element-724" />
      </svg>

      <div className="container mx-auto px-4 max-w-6xl relative" data-id="element-725">
        <div className="text-center mb-10" data-id="element-726">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-727">
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-728"></span>
            Ciencia Apícola
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-729"></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white leading-tight" data-id="element-730">
            El Secreto que las Clínicas de Adelgazamiento
            <br className="hidden md:block" data-id="element-731" />
            <span className="text-[#D4AF37]" data-id="element-732"> No Quieren que Sepas</span>
          </h2>
          <p className="text-green-300 mt-4 max-w-2xl mx-auto text-base leading-relaxed" data-id="element-733">
            La Melitina — el compuesto activo del veneno de abeja — activa
            receptores de calor en la piel que disparan la termogénesis
            localizada y el drenaje linfático. Sin cirugía. Sin pastillas. Solo
            bioquímica natural.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-734">
          {[{
          icon: Flame,
          label: 'Termogénesis',
          desc: 'Activa el calor metabólico local',
          color: 'text-orange-400'
        }, {
          icon: Droplets,
          label: 'Drenaje Linfático',
          desc: 'Elimina líquidos retenidos',
          color: 'text-blue-400'
        }, {
          icon: Zap,
          label: 'Melitina Activa',
          desc: 'Compuesto bioactivo del veneno',
          color: 'text-[#D4AF37]'
        }, {
          icon: Moon,
          label: 'Acción Nocturna',
          desc: 'Trabaja mientras duermes',
          color: 'text-purple-400'
        }].map(({
          icon: Icon,
          label,
          desc,
          color
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 transition-colors" data-id="element-735">
              <div className={`w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mx-auto mb-3`} data-id="element-736">
                <Icon className={`w-6 h-6 ${color}`} data-id="element-737" />
              </div>
              <p className="text-white font-black text-sm mb-1" data-id="element-738">{label}</p>
              <p className="text-green-400/70 text-xs leading-snug" data-id="element-739">{desc}</p>
            </motion.div>)}
        </div>

        {/* Key stat row */}
        <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-8" data-id="element-740">
          {[{
          num: '94%',
          label: 'ven reducción de hinchazón en 7 días'
        }, {
          num: '−8 kg',
          label: 'pérdida media en 21 días de uso'
        }, {
          num: '10,000+',
          label: 'mujeres transformadas en Latinoamérica'
        }].map(({
          num,
          label
        }, i) => <div key={i} className="text-center" data-id="element-741">
              <p className="text-3xl md:text-4xl font-black text-[#D4AF37]" data-id="element-742">
                {num}
              </p>
              <p className="text-green-300/70 text-xs mt-1 leading-snug" data-id="element-743">
                {label}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   2. WHY IT WORKS — "Por Qué StingFit Funciona"
   Cream background, icon grid, brand voice
───────────────────────────────────────────── */
export function WhyItWorks() {
  const reasons = [{
    icon: Activity,
    title: 'Tecnología Transdérmica',
    body: 'Los ingredientes activos penetran directamente a través de la piel hasta la grasa subcutánea. Sin pasar por el sistema digestivo. Absorción máxima.',
    accent: '#D4AF37'
  }, {
    icon: Leaf,
    title: '100% Natural y Tópico',
    body: 'Sin pastillas que estresen tu hígado. Sin dietas que te dejen sin energía. Solo un parche discreto que trabaja en silencio mientras vives tu vida.',
    accent: '#2D5016'
  }, {
    icon: Clock,
    title: '8 Horas de Acción Continua',
    body: 'La fórmula de liberación prolongada actúa durante toda la noche. Te despiertas con la barriga más plana. Cada mañana es una victoria.',
    accent: '#D4AF37'
  }, {
    icon: TrendingDown,
    title: 'Grasa Abdominal Resistente',
    body: 'Especialmente formulado para la grasa visceral y la retención de líquidos — los dos enemigos principales de las mujeres mayores de 35 años.',
    accent: '#2D5016'
  }, {
    icon: Heart,
    title: 'Apoya tu Bienestar General',
    body: 'El drenaje linfático activo reduce la inflamación sistémica, mejora la circulación y te da esa sensación de ligereza que no sentías desde hace años.',
    accent: '#D4AF37'
  }, {
    icon: CheckCircle2,
    title: 'Resultados o Dinero de Vuelta',
    body: '90 días de garantía total. Si no ves resultados, te devolvemos cada centavo. Sin formularios. Sin preguntas. Así de seguros estamos.',
    accent: '#2D5016'
  }];
  return <section className="bg-[#FDFBF7] py-16 relative overflow-hidden" data-id="element-744">
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'radial-gradient(circle at 1px 1px, #2D5016 1px, transparent 0)',
      backgroundSize: '28px 28px'
    }} data-id="element-745"></div>
      <div className="container mx-auto px-4 max-w-6xl relative" data-id="element-746">
        <div className="text-center mb-12" data-id="element-747">
          <span className="inline-flex items-center gap-2 text-[#8B6914] text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-748">
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-749"></span>
            Por Qué Funciona
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-750"></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-751">
            No es Magia. Es <span className="text-[#D4AF37]" data-id="element-752">Bioquímica.</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto" data-id="element-753">
            Cada elemento de StingFit está diseñado con un propósito específico.
            Nada es casualidad.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-754">
          {reasons.map(({
          icon: Icon,
          title,
          body,
          accent
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm" data-id="element-755">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{
            backgroundColor: `${accent}15`
          }} data-id="element-756">
                <Icon className="w-5 h-5" style={{
              color: accent
            }} data-id="element-757" />
              </div>
              <h3 className="font-black text-gray-900 mb-2 text-base" data-id="element-758">
                {title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed" data-id="element-759">{body}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   3. ROUTINE STRIP — "Tu Rutina de 60 Segundos"
   Dark, numbered steps, no images
───────────────────────────────────────────── */
export function RoutineStrip() {
  const steps = [{
    n: '01',
    title: 'Limpia la zona',
    body: 'Lava y seca bien el abdomen antes de dormir. 10 segundos.'
  }, {
    n: '02',
    title: 'Aplica el parche',
    body: 'Pégalo cerca del ombligo. Presiona 5 segundos. Listo.'
  }, {
    n: '03',
    title: 'Duerme y transforma',
    body: 'La fórmula actúa durante 8 horas mientras descansas.'
  }, {
    n: '04',
    title: 'Retira por la mañana',
    body: 'Quítalo al despertar. Nota la diferencia desde el día 1.'
  }];
  return <section className="bg-[#2D5016] py-14 relative overflow-hidden" data-id="element-760">
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: 'repeating-linear-gradient(60deg, #D4AF37 0, #D4AF37 1px, transparent 0, transparent 50%)',
      backgroundSize: '20px 20px'
    }} data-id="element-761"></div>
      <div className="container mx-auto px-4 max-w-6xl relative" data-id="element-762">
        <div className="text-center mb-10" data-id="element-763">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-764">
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-765"></span>
            Uso Diario
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-766"></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white" data-id="element-767">
            Tu Rutina de <span className="text-[#D4AF37]" data-id="element-768">60 Segundos</span>
          </h2>
          <p className="text-green-300 mt-2 text-base" data-id="element-769">
            Tan simple que no hay excusa para no hacerlo.
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-4" data-id="element-770">
          {steps.map(({
          n,
          title,
          body
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -10
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="relative" data-id="element-771">
              {i < steps.length - 1 && <div className="hidden md:block absolute top-8 left-full w-full h-px border-t-2 border-dashed border-[#D4AF37]/30 z-0 -translate-x-1/2" data-id="element-772"></div>}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative z-10 h-full" data-id="element-773">
                <div className="text-5xl font-black text-[#D4AF37]/20 leading-none mb-3 select-none" data-id="element-774">
                  {n}
                </div>
                <h3 className="text-white font-black text-base mb-2" data-id="element-775">
                  {title}
                </h3>
                <p className="text-green-300/70 text-sm leading-relaxed" data-id="element-776">
                  {body}
                </p>
              </div>
            </motion.div>)}
        </div>

        <div className="mt-10 text-center" data-id="element-777">
          <Button size="lg" onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} className="group shadow-xl" data-id="element-778">
            <span className="flex items-center gap-2 font-black" data-id="element-779">
              EMPEZAR MI RUTINA HOY
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-780" />
            </span>
          </Button>
          <p className="text-green-400/60 text-xs mt-3" data-id="element-781">
            Envío gratis · Garantía 90 días · Sin riesgo
          </p>
          <p className="text-green-300 text-xs font-bold mt-1" data-id="element-782">
            💰 Pago contra entrega en la puerta de tu casa
          </p>
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   4. COMPARISON TABLE — "StingFit vs El Resto"
   White bg, branded table, no images
───────────────────────────────────────────── */
export function ComparisonTable() {
  const rows = [{
    feature: 'Resultados visibles',
    stingfit: true,
    dieta: false,
    pastillas: false,
    gym: false
  }, {
    feature: 'Sin efectos secundarios',
    stingfit: true,
    dieta: false,
    pastillas: false,
    gym: true
  }, {
    feature: 'Actúa mientras duermes',
    stingfit: true,
    dieta: false,
    pastillas: false,
    gym: false
  }, {
    feature: 'Sin cambiar tu rutina',
    stingfit: true,
    dieta: false,
    pastillas: true,
    gym: false
  }, {
    feature: 'Drenaje linfático activo',
    stingfit: true,
    dieta: false,
    pastillas: false,
    gym: false
  }, {
    feature: 'Garantía de devolución',
    stingfit: true,
    dieta: false,
    pastillas: false,
    gym: false
  }];
  const cols = [{
    label: 'STINGFIT',
    key: 'stingfit',
    highlight: true
  }, {
    label: 'Dieta',
    key: 'dieta',
    highlight: false
  }, {
    label: 'Pastillas',
    key: 'pastillas',
    highlight: false
  }, {
    label: 'Gimnasio',
    key: 'gym',
    highlight: false
  }];
  return <section className="bg-white py-16" data-id="element-783">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-784">
        <div className="text-center mb-10" data-id="element-785">
          <span className="inline-flex items-center gap-2 text-[#8B6914] text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-786">
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-787"></span>
            Comparativa
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-788"></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-789">
            StingFit vs <span className="text-[#D4AF37]" data-id="element-790">El Resto</span>
          </h2>
          <p className="text-gray-500 mt-2" data-id="element-791">La diferencia habla por sí sola.</p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-lg" data-id="element-792">
          <table className="w-full" data-id="element-793">
            <thead data-id="element-794">
              <tr className="border-b border-gray-100" data-id="element-795">
                <th className="text-left p-4 text-sm font-bold text-gray-500 w-1/2" data-id="element-796">
                  Característica
                </th>
                {cols.map(({
                label,
                highlight
              }) => <th key={label} className={`p-4 text-center text-sm font-black ${highlight ? 'bg-[#2D5016] text-[#D4AF37]' : 'text-gray-400'}`} data-id="element-797">
                    {label}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-798">
              {rows.map(({
              feature,
              ...vals
            }, i) => <tr key={i} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}`} data-id="element-799">
                  <td className="p-4 text-sm font-semibold text-gray-700" data-id="element-800">
                    {feature}
                  </td>
                  {cols.map(({
                key,
                highlight
              }) => <td key={key} className={`p-4 text-center ${highlight ? 'bg-[#2D5016]/5' : ''}`} data-id="element-801">
                      {(vals as any)[key] ? <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${highlight ? 'bg-[#D4AF37] text-white' : 'bg-green-100 text-green-600'} font-black text-sm`} data-id="element-802">
                          ✓
                        </span> : <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-50 text-red-400 font-black text-sm" data-id="element-803">
                          ✗
                        </span>}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>

        <div className="mt-8 text-center" data-id="element-804">
          <Button size="lg" onClick={() => document.getElementById('pricing')?.scrollIntoView({
          behavior: 'smooth'
        })} className="group shadow-xl" data-id="element-805">
            <span className="flex items-center gap-2 font-black" data-id="element-806">
              QUIERO EL MEJOR RESULTADO
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-807" />
            </span>
          </Button>
          <p className="text-xs text-green-700 font-bold mt-3" data-id="element-808">
            💰 Pago contra entrega en la puerta de tu casa
          </p>
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   5. URGENCY BANNER — "¿Para Quién es StingFit?"
   Gold accent, text-only, high conversion
───────────────────────────────────────────── */
export function ForWhomBanner() {
  const forYou = ['Tienes más de 35 años y la barriga no baja con dieta', 'Sientes hinchazón o retención de líquidos constante', 'Has probado de todo y nada funciona a largo plazo', 'No tienes tiempo para el gimnasio todos los días', 'Quieres resultados reales sin pastillas ni cirugías'];
  return <section className="bg-gradient-to-br from-[#D4AF37] to-[#B5952F] py-14" data-id="element-809">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-810">
        <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-811">
          <div data-id="element-812">
            <span className="inline-block bg-white/20 text-white text-xs font-black uppercase tracking-[0.15em] px-3 py-1.5 rounded-full mb-4" data-id="element-813">
              ¿Es Para Ti?
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-4" data-id="element-814">
              StingFit es para ti si…
            </h2>
            <p className="text-white/80 text-base leading-relaxed" data-id="element-815">
              No somos para todo el mundo. Somos para mujeres que ya están
              hartas de promesas vacías y quieren resultados de verdad.
            </p>
          </div>
          <div className="space-y-3" data-id="element-816">
            {forYou.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: i * 0.08
          }} className="flex items-start gap-3 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3" data-id="element-817">
                <span className="w-5 h-5 rounded-full bg-white text-[#B5952F] flex items-center justify-center flex-shrink-0 font-black text-xs mt-0.5" data-id="element-818">
                  ✓
                </span>
                <span className="text-white font-semibold text-sm leading-snug" data-id="element-819">
                  {item}
                </span>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   6. PULL QUOTE — Single powerful testimonial
   Full-width dark, editorial, no images
───────────────────────────────────────────── */
export function PullQuote() {
  return <section className="bg-[#1A3009] py-16 relative overflow-hidden" data-id="element-820">
      {/* Large decorative quote mark */}
      <div className="absolute left-8 top-8 text-[#D4AF37]/10 select-none pointer-events-none" style={{
      fontSize: '20rem',
      lineHeight: 1,
      fontFamily: 'Georgia, serif'
    }} data-id="element-821">
        "
      </div>
      <div className="container mx-auto px-4 max-w-4xl relative text-center" data-id="element-822">
        <div className="flex justify-center mb-6" data-id="element-823">
          {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-6 h-6 fill-[#D4AF37] text-[#D4AF37]" data-id="element-824" />)}
        </div>
        <blockquote className="text-2xl md:text-4xl font-black text-white leading-tight mb-8 relative z-10" data-id="element-825">
          "Llevaba 4 años intentando perder la barriga postparto. Probé todo:
          dietas, pastillas, fajas. Nada funcionó. En 3 semanas con StingFit
          perdí <span className="text-[#D4AF37]" data-id="element-826">9 kg y 11 cm de cintura.</span>{' '}
          Mi marido pensó que me había operado."
        </blockquote>
        <div className="flex items-center justify-center gap-4" data-id="element-827">
          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 border-2 border-[#D4AF37]/40 flex items-center justify-center" data-id="element-828">
            <span className="text-[#D4AF37] font-black text-lg" data-id="element-829">M</span>
          </div>
          <div className="text-left" data-id="element-830">
            <p className="text-white font-black" data-id="element-831">María José L.</p>
            <p className="text-green-400 text-sm" data-id="element-832">
              43 años · CDMX · Clienta verificada
            </p>
          </div>
        </div>
        <div className="mt-8 inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2" data-id="element-833">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-834"></span>
          <span className="text-green-300 text-xs font-semibold" data-id="element-835">
            Resultado verificado con fotos antes/después
          </span>
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   7. INGREDIENTS SPOTLIGHT
   Cream bg, ingredient cards, no images
───────────────────────────────────────────── */
export function IngredientsSpotlight() {
  const ingredients = [{
    emoji: '🐝',
    name: 'Veneno de Abeja (Apitoxina)',
    role: 'Ingrediente Estrella',
    benefit: 'Activa la termogénesis localizada y estimula la circulación linfática. El secreto detrás de los resultados.',
    highlight: true
  }, {
    emoji: '🌿',
    name: 'Extracto de Jengibre',
    role: 'Anti-inflamatorio',
    benefit: 'Reduce la inflamación abdominal y mejora la digestión. Efecto visible desde los primeros días.',
    highlight: false
  }, {
    emoji: '🍃',
    name: 'Té Verde Matcha',
    role: 'Quemador Natural',
    benefit: 'EGCG y cafeína natural que potencian la oxidación de grasas durante las horas de sueño.',
    highlight: false
  }, {
    emoji: '🌱',
    name: 'Aceite de Árbol de Té',
    role: 'Antibacteriano',
    benefit: 'Protege la piel durante el uso prolongado. Garantiza comodidad y adherencia perfecta.',
    highlight: false
  }, {
    emoji: '💧',
    name: 'Ácido Hialurónico',
    role: 'Hidratante',
    benefit: 'Mantiene la piel hidratada y elástica. Tu piel mejora mientras pierdes centímetros.',
    highlight: false
  }, {
    emoji: '🔥',
    name: 'Capsaicina Natural',
    role: 'Termogénico',
    benefit: 'Amplifica el efecto calórico del veneno de abeja. Doble acción termogénica para resultados más rápidos.',
    highlight: false
  }];
  return <section className="bg-[#FDFBF7] py-16 relative overflow-hidden" data-id="element-836">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-837">
        <div className="text-center mb-12" data-id="element-838">
          <span className="inline-flex items-center gap-2 text-[#8B6914] text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-839">
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-840"></span>
            Fórmula Exclusiva
            <span className="w-8 h-px bg-[#D4AF37]" data-id="element-841"></span>
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-842">
            6 Ingredientes. <span className="text-[#D4AF37]" data-id="element-843">1 Resultado.</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto" data-id="element-844">
            Cada ingrediente fue seleccionado por su eficacia comprobada. Juntos
            crean una sinergia que ningún producto por separado puede igualar.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-845">
          {ingredients.map(({
          emoji,
          name,
          role,
          benefit,
          highlight
        }, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.07
        }} className={`rounded-2xl p-6 border-2 transition-all ${highlight ? 'border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 shadow-lg shadow-[#D4AF37]/10' : 'border-gray-100 bg-white shadow-sm'}`} data-id="element-846">
              {highlight && <div className="inline-block bg-[#D4AF37] text-white text-xs font-black px-3 py-1 rounded-full mb-3 uppercase tracking-wide" data-id="element-847">
                  ⭐ Ingrediente Principal
                </div>}
              <div className="text-4xl mb-3" data-id="element-848">{emoji}</div>
              <div className="text-xs font-black text-[#D4AF37] uppercase tracking-wider mb-1" data-id="element-849">
                {role}
              </div>
              <h3 className="font-black text-gray-900 text-base mb-2" data-id="element-850">
                {name}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed" data-id="element-851">{benefit}</p>
            </motion.div>)}
        </div>

        <div className="mt-10 bg-[#2D5016] rounded-2xl p-6 text-center" data-id="element-852">
          <p className="text-white font-black text-lg mb-1" data-id="element-853">
            Fórmula Patentada · Registro COFEPRIS
          </p>
          <p className="text-green-300 text-sm" data-id="element-854">
            Todos los ingredientes son de origen natural y han sido testados
            dermatológicamente.
          </p>
        </div>
      </div>
    </section>;
}
/* ─────────────────────────────────────────────
   8. SOCIAL TICKER — Scrolling proof strip
───────────────────────────────────────────── */
export function SocialTicker() {
  const items = ['⭐⭐⭐⭐⭐ "Perdí 7 kg en 3 semanas" — Ana R.', '🔥 +10,000 clientas satisfechas en Latinoamérica', '⭐⭐⭐⭐⭐ "Mi barriga desapareció" — Carmen L.', '✅ Garantía 90 días sin preguntas', '⭐⭐⭐⭐⭐ "-12 cm de cintura en 30 días" — Laura M.', '🚚 Envío gratis a todo México', '⭐⭐⭐⭐⭐ "El mejor producto que he probado" — Sofía G.', '🏆 Producto #1 en drenaje linfático', '⭐⭐⭐⭐⭐ "Resultados desde el día 7" — María J.', '🔒 Pago 100% seguro · SSL 256-bit'];
  return <div className="bg-[#2D5016] border-y border-[#D4AF37]/20 py-2.5 overflow-hidden" data-id="element-855">
      <div className="flex animate-marquee whitespace-nowrap" data-id="element-856">
        {[...items, ...items].map((item, i) => <span key={i} className="inline-flex items-center gap-2 text-green-100 text-xs font-semibold px-8" data-id="element-857">
            {item}
            <span className="text-[#D4AF37] mx-2" data-id="element-858">◆</span>
          </span>)}
      </div>
    </div>;
}